import { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import PublicNav from "@/components/PublicNav";
import { ListingThumb, Stars } from "@/pages/Market";
import { ArrowLeft, CreditCard, Tag } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function MarketListing() {
  const { listingId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [listing, setListing] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [error, setError] = useState(null);
  const [actionError, setActionError] = useState("");
  const [ordering, setOrdering] = useState(false);

  useEffect(() => {
    api
      .get(`/market/listings/${listingId}`)
      .then((r) => setListing(r.data))
      .catch((err) => setError(err?.response?.status || 500));
    api
      .get(`/market/listings/${listingId}/reviews`)
      .then((r) => setReviews(r.data))
      .catch(() => {});
  }, [listingId]);

  const order = async () => {
    if (!user) {
      navigate("/login");
      return;
    }
    setActionError("");
    setOrdering(true);
    try {
      const res = await api.post("/market/orders", {
        listing_id: listing.listing_id,
        origin_url: window.location.origin,
      });
      window.location.href = res.data.approval_url;
    } catch (err) {
      setActionError(formatApiError(err));
      setOrdering(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50">
        <PublicNav />
        <div data-testid="listing-not-found" className="max-w-3xl mx-auto px-6 py-24 text-center text-slate-500">
          Annonce introuvable.{" "}
          <Link to="/marche" className="text-brand-turquoise font-medium hover:underline">Retour au marché</Link>
        </div>
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="min-h-screen bg-slate-50">
        <PublicNav />
        <div className="max-w-4xl mx-auto px-6 py-24 flex justify-center">
          <div className="w-10 h-10 border-4 border-slate-200 border-t-brand-turquoise rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div data-testid="market-listing-page" className="min-h-screen bg-slate-50">
      <PublicNav />

      <div className="max-w-5xl mx-auto px-6 py-8">
        <button
          data-testid="listing-back-button"
          onClick={() => navigate("/marche")}
          className="flex items-center gap-1.5 text-sm text-slate-600 hover:text-brand-navy mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Retour au marché
        </button>

        <div className="grid md:grid-cols-5 gap-8">
          <div className="md:col-span-3">
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <ListingThumb images={listing.images} canSee={Boolean(user)} />
              {user && listing.images?.length > 1 && (
                <div className="flex gap-2 p-3 overflow-x-auto">
                  {listing.images.slice(1).map((img) => (
                    <img
                      key={img.file_id}
                      src={`${API_BASE}/api/files/${img.file_id}/download`}
                      alt=""
                      className="w-20 h-20 object-cover rounded-lg border border-slate-200"
                      loading="lazy"
                    />
                  ))}
                </div>
              )}
              <div className="p-6">
                <div className="flex items-start justify-between gap-3">
                  <h1 data-testid="listing-title" className="text-2xl font-extrabold text-brand-navy">{listing.title}</h1>
                  <span className="text-[10px] font-semibold uppercase tracking-wide bg-brand-sky text-brand-navy rounded-full px-2.5 py-1 shrink-0">
                    {listing.kind === "produit" ? "Produit" : "Service"}
                  </span>
                </div>
                <div className="flex items-center gap-3 mt-2">
                  {listing.rating_count > 0 ? (
                    <span data-testid="listing-rating" className="inline-flex items-center gap-1.5 text-sm text-slate-600">
                      <Stars value={listing.rating_avg} />
                      {listing.rating_avg}/5 ({listing.rating_count} avis)
                    </span>
                  ) : (
                    <span className="text-sm text-slate-400">Aucune évaluation pour le moment</span>
                  )}
                </div>
                <p data-testid="listing-description" className="text-sm text-slate-600 leading-relaxed whitespace-pre-line mt-4">
                  {listing.description}
                </p>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 mt-6">
              <div className="px-6 py-4 border-b border-slate-100">
                <h2 className="font-semibold text-brand-navy">Évaluations ({reviews.length})</h2>
              </div>
              {reviews.length === 0 ? (
                <p data-testid="reviews-empty" className="px-6 py-8 text-sm text-slate-400 text-center">
                  Les évaluations des acheteurs apparaîtront ici.
                </p>
              ) : (
                reviews.map((r) => (
                  <div key={r.review_id} data-testid={`review-${r.review_id}`} className="px-6 py-4 border-b border-slate-50">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-brand-navy">{r.reviewer_name}</span>
                      <Stars value={r.rating} size="w-3.5 h-3.5" />
                    </div>
                    {r.comment && <p className="text-sm text-slate-600 mt-1">{r.comment}</p>}
                    <p className="text-xs text-slate-400 mt-1">
                      {r.created_at ? new Date(r.created_at).toLocaleDateString("fr-CA") : ""}
                    </p>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="md:col-span-2 space-y-4">
            <div className="bg-white rounded-xl border border-slate-200 p-6 sticky top-20">
              <p data-testid="listing-price" className="text-3xl font-extrabold text-brand-navy">
                {listing.price} $ <span className="text-base font-semibold text-slate-500">{listing.currency}</span>
              </p>
              {actionError && (
                <p data-testid="order-error" className="text-xs text-red-600 mt-3">{actionError}</p>
              )}
              {listing.is_mine ? (
                <button
                  data-testid="listing-manage-button"
                  onClick={() => navigate("/marche?tab=annonces")}
                  className="mt-4 w-full border border-slate-300 text-brand-navy font-semibold rounded-lg px-4 py-3 hover:bg-slate-100 transition-colors"
                >
                  Gérer mon annonce
                </button>
              ) : (
                <button
                  data-testid="order-button"
                  onClick={order}
                  disabled={ordering}
                  className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-brand-blue text-white font-semibold rounded-lg px-4 py-3 hover:bg-brand-navy transition-colors disabled:opacity-50"
                >
                  <CreditCard className="w-4 h-4" />
                  {ordering ? "Redirection vers PayPal…" : "Commander avec PayPal"}
                </button>
              )}
              <p className="text-xs text-slate-400 mt-3 text-center">Paiement sécurisé par PayPal</p>
            </div>

            {listing.seller?.nexa_id && (
              <Link
                data-testid="seller-profile-link"
                to={`/profil/${listing.seller.nexa_id}`}
                className="flex items-center gap-3 bg-white rounded-xl border border-slate-200 p-5 hover:border-brand-turquoise hover:shadow-md transition-all duration-200"
              >
                <div className="w-10 h-10 rounded-full bg-brand-turquoise/15 flex items-center justify-center text-brand-turquoise font-bold shrink-0">
                  {listing.seller.name?.[0]?.toUpperCase() || "?"}
                </div>
                <div className="min-w-0">
                  <p className="font-semibold text-brand-navy text-sm truncate">{listing.seller.name}</p>
                  <p className="text-xs text-slate-400 flex items-center gap-1">
                    <Tag className="w-3 h-3" />@{listing.seller.nexa_id}
                  </p>
                </div>
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
